class FreeGiftUpsell extends HTMLElement {
  static hasAutoAdded = false; // Track if we've already auto-added in this session
  static globalProcessing = false; // Prevent any gift operations during cart updates

  // ------------------------------------------------------------------
  // Customer decline + colour selection tracking.
  //
  // The gift used to be put straight back into the cart the moment the customer
  // pressed checkout, so removing it from the cart drawer had no lasting effect.
  // We now remember three things for the length of the session:
  //   - ADDED_KEY:    a gift that we placed in the cart, and which variant it was
  //   - DECLINE_KEY:  the customer took our gift back out of the cart
  //   - SELECTED_KEY: the colour/variant the customer last picked in the UI
  // If the marker says a gift should be in the cart and the cart no longer holds
  // one, the customer removed it, and nothing may add it back automatically —
  // until they actively re-select a colour (that clears DECLINE_KEY).
  // SELECTED_KEY survives cart-drawer remounts so beige does not snap back to
  // the Liquid default (black) after an unrelated cart update.
  // The markers live in sessionStorage so they survive the drawer re-rendering
  // itself and any full page load. A private-mode failure falls back to memory.
  // ------------------------------------------------------------------
  static DECLINE_KEY = 'onyx_free_gift_declined';
  static ADDED_KEY = 'onyx_free_gift_added';
  static SELECTED_KEY = 'onyx_free_gift_selected';
  static memoryStore = {};

  static readFlag(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return FreeGiftUpsell.memoryStore[key] || null;
    }
  }

  static writeFlag(key, value) {
    if (value === null) {
      delete FreeGiftUpsell.memoryStore[key];
    } else {
      FreeGiftUpsell.memoryStore[key] = value;
    }
    try {
      if (value === null) {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, value);
      }
    } catch (error) {
      // sessionStorage unavailable, the in-memory copy stands in
    }
  }

  static isDeclined() {
    return FreeGiftUpsell.readFlag(FreeGiftUpsell.DECLINE_KEY) === '1';
  }

  static setDeclined() {
    FreeGiftUpsell.writeFlag(FreeGiftUpsell.DECLINE_KEY, '1');
    FreeGiftUpsell.writeFlag(FreeGiftUpsell.ADDED_KEY, null);
    FreeGiftUpsell.hasAutoAdded = false;
  }

  // Called when the customer actively asks for the gift again
  static clearDeclined() {
    FreeGiftUpsell.writeFlag(FreeGiftUpsell.DECLINE_KEY, null);
  }

  static markGiftAdded(variantId) {
    FreeGiftUpsell.writeFlag(FreeGiftUpsell.ADDED_KEY, String(variantId));
  }

  static clearGiftAdded() {
    FreeGiftUpsell.writeFlag(FreeGiftUpsell.ADDED_KEY, null);
  }

  static wasGiftAdded() {
    return FreeGiftUpsell.readFlag(FreeGiftUpsell.ADDED_KEY) !== null;
  }

  static getSelectedVariantId() {
    return FreeGiftUpsell.readFlag(FreeGiftUpsell.SELECTED_KEY);
  }

  static setSelectedVariantId(variantId) {
    if (variantId === null || variantId === undefined || variantId === '') {
      FreeGiftUpsell.writeFlag(FreeGiftUpsell.SELECTED_KEY, null);
      return;
    }
    FreeGiftUpsell.writeFlag(FreeGiftUpsell.SELECTED_KEY, String(variantId));
  }
  
  constructor() {
    super();
    this.radioButtons = this.querySelectorAll('input[type="radio"]');
    this.cards = this.querySelectorAll('.free-gift-card');
    this.handleRadioChange = this.handleRadioChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.confirmGift = this.confirmGift.bind(this);
    this.cartDrawer = document.querySelector('cart-drawer');
    this.isProcessing = false;
    this.pendingUpdate = null;
    // True while this component is removing the gift itself (swapping colours, or
    // pulling it back out below the threshold), so that removal is not mistaken
    // for the customer declining the gift.
    this.selfRemoving = false;

    // Confirm mode: the gift is only added to the cart when the customer presses
    // a card's "add" button (after picking a colour). No auto-add, no instant add on swatch click.
    this.confirmMode = this.hasAttribute('data-confirm-mode');
    this.confirmBtns = this.querySelectorAll('[data-free-gift-confirm]');

    // Checkout-add mode: there is no per-card add button. The customer just picks a
    // colour (black is pre-selected by default) and the selected gift is added to the
    // cart the moment they press the cart drawer's checkout button.
    this.checkoutAddMode = this.hasAttribute('data-checkout-add');
    this._checkoutButton = null;
    this._onCheckoutClick = null;
    this._proceedingCheckout = false;

    this.setupErrorCleaner();
  }
  
  // Setup interval to clear error messages on free gift line items
  setupErrorCleaner() {
    // Check for and remove error messages on free gift items every 500ms
    this.errorCleanerInterval = setInterval(() => {
      this.clearFreeGiftErrors();
    }, 500);
  }
  
  // Clear any error messages showing on free gift line items
  clearFreeGiftErrors() {
    if (!this.cartDrawer) return;
    
    try {
      // Find all line items in the cart
      const lineItems = this.cartDrawer.querySelectorAll('line-item');
      
      lineItems.forEach(lineItem => {
        // Check if this line item has the free gift property
        const lineItemData = lineItem.querySelector('[data-line-item-key]');
        if (!lineItemData) return;
        
        // Check if this is a free gift by looking for the property indicator
        const productInfo = lineItem.querySelector('.line-item__info');
        if (!productInfo) return;
        
        // Check for our free gift badge or property
        const isFreeGift = lineItem.querySelector('.badge')?.textContent?.includes('Gratis') ||
                          lineItem.querySelector('.badge')?.textContent?.includes('Free') ||
                          lineItem.querySelector('.badge')?.textContent?.includes('Cadeautje');
        
        if (isFreeGift) {
          // Remove any error messages on this free gift item
          const errorMsg = productInfo.querySelector('[role="alert"].text-error');
          if (errorMsg) {
            console.log('Clearing error message from free gift item');
            errorMsg.remove();
          }
        }
      });
    } catch (error) {
      // Silently fail - don't log errors
    }
  }

  async connectedCallback() {
    this.radioButtons.forEach(radio => radio.addEventListener('change', this.handleRadioChange));
    this.updateSelectedState();
    this.setupColorSwatches();
    this.setupCardClickPrevention();

    // Wire up each card's add button (confirm mode only)
    if (this.confirmBtns && this.confirmBtns.length) {
      this.confirmBtns.forEach(btn => btn.addEventListener('click', this.confirmGift));
    }

    // Wire up checkout-add mode: intercept the cart drawer's checkout button so the
    // selected gift is dropped into the cart right before checkout proceeds.
    if (this.checkoutAddMode) {
      this.setupCheckoutAdd();
    }

    // Check current cart state and update UI
    await this.checkCartAndUpdateUI();

    // Confirm mode: restore the customer's colour BEFORE progress-bar logic can
    // pre-select black from the Liquid default on remount.
    if (this.confirmMode) {
      await this.initConfirmMode();
    }
    
    // Update progress bars initially
    this.updateProgressBars();
    
    // Watch for cart changes to update progress bars
    this.setupCartObserver();

    // CONFIRM MODE: never auto-add. The customer picks a colour and then presses
    // the confirm button (or checkout) to add the gift. Stop here so the legacy
    // auto-add never runs.
    if (this.confirmMode) {
      return;
    }

    // Check if we're in threshold mode
    const isThresholdMode = this.closest('.cart-drawer__free-gift')?.hasAttribute('data-threshold');
    
    // In threshold mode: check if threshold is met and auto-add
    // In product mode: auto-add first gift if threshold met
    if (isThresholdMode) {
      // Threshold mode: immediately check and auto-add if threshold is met
      if (!this.isThresholdModeDisabled()) {
        console.log('Initial load: Threshold mode enabled and threshold met - attempting auto-add');
        setTimeout(() => this.autoAddFirstGiftThresholdMode(), 500);
      }
    } else {
      // Product trigger mode: auto-add first pre-selected gift
      if (!this.isThresholdModeDisabled()) {
        console.log('Initial load: Product trigger mode - attempting auto-add');
        setTimeout(() => this.autoAddFirstGift(), 500);
      }
    }
  }

  // Refresh the cart drawer using the theme's proper mechanism.
  // (The old code called this.cartDrawer.forceUpdate(), which does not exist on
  // the cart-drawer element and silently threw, leaving the drawer out of sync.)
  refreshCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
  
  // Prevent clicking cards when threshold mode is disabled
  setupCardClickPrevention() {
    this.cards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (this.isThresholdModeDisabled()) {
          e.preventDefault();
          e.stopPropagation();
          console.log('Threshold not met, cannot select gift');
        }
      });
    });
  }
  
  // Setup observer to watch for cart changes
  setupCartObserver() {
    if (this.cartDrawer) {
      // Listen for any cart update events
      const events = ['cart:refresh', 'cart:change', 'cart:update', 'variant:add'];
      events.forEach(eventName => {
        this.cartDrawer.addEventListener(eventName, async () => {
          await this.syncFulfilledVisibility();
          await this.updateProgressBars();
          // After updating progress bars, check if we should auto-add
          await this.checkAndAutoAddGift();
        });
      });
      
      // Also watch for DOM changes in cart total price
      const observer = new MutationObserver(async () => {
        await this.syncFulfilledVisibility();
        await this.updateProgressBars();
        await this.checkAndAutoAddGift();
      });
      
      const cartPriceElements = this.cartDrawer.querySelectorAll('.cart-price, .h5.cart-price');
      cartPriceElements.forEach(el => {
        observer.observe(el, { childList: true, characterData: true, subtree: true });
      });
      
      this.observer = observer;
    }
    
    // Also listen to document-level cart events. Store the handler so it can be
    // removed on disconnect (the component re-mounts on cart updates, and leaking
    // a listener each time would fire redundant cart fetches).
    this._onDocCartChange = async () => {
      await this.syncFulfilledVisibility();
      await this.updateProgressBars();
      await this.checkAndAutoAddGift();
    };
    document.addEventListener('cart:change', this._onDocCartChange);
  }

  // Show/hide the gift selector based on whether a free gift is actually in the cart.
  // This is the source of truth for the is-fulfilled (CSS hidden) state, because the
  // theme's _onCartChanged only re-renders the gift section when its title or card
  // count changes - it never notices a class-only change, so after removing the gift
  // the server-set is-fulfilled class would otherwise stay stuck on.
  async syncFulfilledVisibility() {
    const hasGift = await this.readGiftState();
    if (hasGift === null) return;
    if (!this.confirmMode) return;

    const container = this.closest('.cart-drawer__free-gift');
    if (!container) return;

    container.classList.toggle('is-fulfilled', hasGift);
  }

  // Single place where the cart is read for gift purposes. Returns true/false,
  // or null when the cart could not be read.
  //
  // This is also where a customer removal is detected: if we recorded that a gift
  // was placed in the cart and the cart no longer contains one, the customer took
  // it out. From that point on nothing may add it back on its own. Checking the
  // recorded marker rather than an in-memory previous value matters, because the
  // cart drawer re-renders this element on every cart change and a component
  // instance therefore never survives long enough to observe the transition.
  async readGiftState() {
    let cart;
    try {
      cart = await fetch('/cart.js').then(res => res.json());
    } catch (error) {
      return null;
    }

    const hasGift = cart.items.some(item => item.properties && item.properties._is_free_gift);

    if (hasGift) {
      // Keep the marker in step with what is actually in the cart
      const gift = cart.items.find(item => item.properties && item.properties._is_free_gift);
      FreeGiftUpsell.markGiftAdded(gift.variant_id);
      FreeGiftUpsell.setSelectedVariantId(gift.variant_id);
      // Gift is back in the cart, so a previous decline no longer applies
      FreeGiftUpsell.clearDeclined();
    } else if (FreeGiftUpsell.wasGiftAdded() && !this.selfRemoving && !FreeGiftUpsell.globalProcessing) {
      if (this.checkoutAddMode) {
        // Checkout-add mode: the gift is meant to be (re)applied when the customer
        // presses checkout. Removing it from the drawer must not permanently refuse
        // it for the rest of the session — that left eligible carts without a gift
        // after checkout → back → remove → checkout again.
        console.log('Free gift removed in checkout-add mode - will offer again on next checkout');
        FreeGiftUpsell.clearGiftAdded();
        FreeGiftUpsell.hasAutoAdded = false;
      } else {
        console.log('Free gift was removed by the customer - re-select a colour to add it again');
        FreeGiftUpsell.setDeclined();
      }
    }

    return hasGift;
  }
  
  // Check if we should auto-add gift (for threshold mode)
  async checkAndAutoAddGift() {
    // Confirm mode never auto-adds (gift is added only via a card's add button).
    if (this.confirmMode) {
      return;
    }

    // The customer already removed this gift once. Leave it out.
    if (FreeGiftUpsell.isDeclined()) {
      return;
    }

    const isThresholdMode = this.closest('.cart-drawer__free-gift')?.hasAttribute('data-threshold');
    
    if (isThresholdMode && !this.isThresholdModeDisabled()) {
      // Check if gift is actually in cart (don't just rely on hasAutoAdded flag)
      try {
        const currentCart = await fetch('/cart.js').then(res => res.json());
        const hasFreeGift = currentCart.items.some(item => 
          item.properties && item.properties._is_free_gift
        );
        
        if (!hasFreeGift) {
          console.log('Threshold reached and no gift in cart - triggering auto-add');
          await this.autoAddFirstGiftThresholdMode();
        } else if (!FreeGiftUpsell.hasAutoAdded) {
          // Gift is in cart but flag wasn't set - update it
          FreeGiftUpsell.hasAutoAdded = true;
          console.log('Gift already in cart - flag updated');
        }
      } catch (error) {
        console.error('Error checking cart for gift:', error);
      }
    }
  }
  
  // Check if we're in threshold mode and if it's disabled
  isThresholdModeDisabled() {
    const container = this.closest('.cart-drawer__free-gift');
    return container && container.classList.contains('is-disabled');
  }
  
  // Update all progress bars based on current cart
  async updateProgressBars() {
    const progressBars = this.querySelectorAll('.progress-bar__indicator');
    if (progressBars.length === 0) return;
    
    try {
      const currentCart = await fetch('/cart.js').then(res => res.json());
      const cartTotal = currentCart.total_price;
      
      // Get threshold from first progress bar's parent card
      const firstCard = this.querySelector('.free-gift-card');
      if (!firstCard) return;
      
      // Extract threshold from data attribute or compute from visible progress bar
      const container = this.closest('.cart-drawer__free-gift');
      const thresholdAttr = container?.getAttribute('data-threshold');
      
      if (thresholdAttr) {
        const threshold = parseInt(thresholdAttr);
        const progress = Math.min((cartTotal * 100) / threshold, 100);
        const thresholdMet = cartTotal >= threshold;
        
        // Update progress bars
        progressBars.forEach(bar => {
          bar.style.width = progress + '%';
        });
        
        // Toggle disabled state on container
        if (container) {
          const wasDisabled = container.classList.contains('is-disabled');
          
          if (thresholdMet) {
            container.classList.remove('is-disabled');
            container.style.opacity = '';
            container.style.pointerEvents = '';
            console.log('Threshold met! Gifts are now enabled.');

            if (this.confirmMode) {
              // Confirm mode: when the threshold just unlocked, pre-highlight the
              // first colour so the customer only needs to press add. Never auto-add.
              if (wasDisabled) {
                this.preselectFirstChoice();
              }
            } else if (wasDisabled && !FreeGiftUpsell.hasAutoAdded) {
              // Legacy: auto-add the first gift on unlock
              setTimeout(() => this.autoAddFirstGiftThresholdMode(), 500);
            }
          } else {
            container.classList.add('is-disabled');
            console.log('Below threshold. Gifts disabled. Progress:', progress + '%');

            if (this.confirmMode) {
              // Confirm mode: pull any gift back out of the cart so the customer
              // cannot keep a free gift below the threshold.
              this.removeGiftIfBelowThreshold();
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to update progress bars:', error);
    }
  }

  disconnectedCallback() {
    this.radioButtons.forEach(radio => radio.removeEventListener('change', this.handleRadioChange));
    if (this.pendingUpdate) {
      clearTimeout(this.pendingUpdate);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.errorCleanerInterval) {
      clearInterval(this.errorCleanerInterval);
    }
    if (this._onDocCartChange) {
      document.removeEventListener('cart:change', this._onDocCartChange);
    }
    this.teardownCheckoutAdd();
  }
  
  // Check cart and update UI to show if gift is already added
  async checkCartAndUpdateUI() {
    try {
      const freeGiftItem = await this.readGiftState();

      if (freeGiftItem) {
        // Gift is in cart - keep cards interactive
        FreeGiftUpsell.hasAutoAdded = true;
        console.log('Gift found in cart during UI check');
      } else {
        // No gift in cart - ensure cards are enabled
        this.cards.forEach(card => {
          card.style.opacity = '1';
          card.style.pointerEvents = 'auto';
        });
        console.log('No gift in cart during UI check');
      }
    } catch (error) {
      console.error('Error checking cart:', error);
    }
  }

  setLoading(isLoading) {
    this.toggleAttribute('loading', isLoading);
    if (this.cartDrawer) {
      this.cartDrawer.toggleAttribute('loading', isLoading);
    }
  }

  updateSelectedState() {
    this.cards.forEach(card => {
      const radio = card.querySelector('input[type="radio"]');
      if (radio.checked) {
        card.classList.add('is-selected');
      } else {
        card.classList.remove('is-selected');
      }
    });
  }

  // Clear pre-selections when threshold unlocks (so user must actively choose)
  clearPreSelections() {
    console.log('Clearing pre-selections - user must actively choose gift');
    
    // Uncheck all radio buttons
    this.radioButtons.forEach(radio => {
      radio.checked = false;
    });
    
    // Remove selected class from all cards
    this.cards.forEach(card => {
      card.classList.remove('is-selected');
    });
  }
  
  // Auto-add first pre-selected gift if none exists in cart (product trigger mode)
  async autoAddFirstGift() {
    // The customer already removed this gift once. Leave it out.
    if (FreeGiftUpsell.isDeclined()) {
      console.log('Skipping auto-add: customer removed the gift');
      return;
    }

    // Prevent running if already processing or already auto-added
    if (FreeGiftUpsell.globalProcessing || FreeGiftUpsell.hasAutoAdded) {
      console.log('Skipping auto-add: already added or processing');
      return;
    }
    
    try {
      FreeGiftUpsell.globalProcessing = true;
      
      // Check if there's a pre-selected radio button
      const selectedRadio = this.querySelector('input[type="radio"]:checked');
      if (!selectedRadio) {
        console.log('No pre-selected radio button found');
        FreeGiftUpsell.globalProcessing = false;
        return;
      }
      
      // Get current cart to check for existing free gifts
      const currentCart = await fetch('/cart.js').then(res => res.json());
      const hasFreeGift = currentCart.items.some(item => 
        item.properties && item.properties._is_free_gift
      );
      
      // Only auto-add if no free gift exists
      if (!hasFreeGift) {
        const variantId = selectedRadio.value;
        
        if (variantId && variantId !== 'undefined') {
          const variantIdInt = parseInt(variantId, 10);
          console.log('Auto-adding first pre-selected gift. Variant ID:', variantIdInt);
          
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: variantIdInt,
                quantity: 1,
                properties: {
                  _is_free_gift: true
                }
              }]
            })
          });
          
          if (response.ok) {
            FreeGiftUpsell.hasAutoAdded = true;
            FreeGiftUpsell.markGiftAdded(variantIdInt);
            console.log('Gift auto-added successfully');
            
            // Trigger cart update FIRST
            if (this.cartDrawer) {
              this.refreshCartDrawer();
            }
            
            // Wait for cart drawer to update
            await new Promise(resolve => setTimeout(resolve, 1200));
            
            // Don't disable UI - keep cards interactive so users can change selection
            console.log('Gift added successfully - cards remain interactive');
          } else {
            console.error('Failed to add gift, response not ok');
          }
        }
      } else {
        // Gift already exists
        console.log('Gift already in cart');
        FreeGiftUpsell.hasAutoAdded = true;
        // Don't disable UI - users should still be able to change their gift
      }
    } catch (error) {
      console.error('Failed to auto-add first gift:', error);
    } finally {
      FreeGiftUpsell.globalProcessing = false;
    }
  }

  // Auto-add first gift when threshold is met (threshold mode)
  async autoAddFirstGiftThresholdMode() {
    // The customer already removed this gift once. Leave it out.
    if (FreeGiftUpsell.isDeclined()) {
      console.log('Skipping threshold auto-add: customer removed the gift');
      return;
    }

    // Prevent running if already processing
    if (FreeGiftUpsell.globalProcessing) {
      console.log('Skipping threshold auto-add: already processing');
      return;
    }
    
    try {
      FreeGiftUpsell.globalProcessing = true;
      
      // Get current cart to check for existing free gifts
      const currentCart = await fetch('/cart.js').then(res => res.json());
      const hasFreeGift = currentCart.items.some(item => 
        item.properties && item.properties._is_free_gift
      );
      
      // If gift already exists in cart, mark as added and exit
      if (hasFreeGift) {
        console.log('Gift already in cart, marking as auto-added - keeping cards interactive');
        FreeGiftUpsell.hasAutoAdded = true;
        FreeGiftUpsell.globalProcessing = false;
        return;
      }
      
      // Only auto-add if no free gift exists
      if (!hasFreeGift) {
        // Find first available gift card and get its first variant
        const firstCard = this.querySelector('.free-gift-card');
        if (!firstCard) {
          console.log('No gift cards found');
          FreeGiftUpsell.globalProcessing = false;
          return;
        }
        
        // Priority order for getting variant ID:
        // 1. First available color swatch (for products with color variants)
        // 2. Card's data-variant-id attribute
        // 3. Radio button value
        let variantId = null;
        let selectedSwatch = null;
        
        // Try color swatches first (for products with color variants)
        const firstAvailableSwatch = firstCard.querySelector('.free-gift-card__color-swatch:not(.is-disabled)');
        
        if (firstAvailableSwatch) {
          variantId = firstAvailableSwatch.getAttribute('data-variant-id');
          selectedSwatch = firstAvailableSwatch;
          console.log('Using first available color swatch variant:', variantId);
        } else {
          // No color swatches or all disabled - try card's data-variant-id
          variantId = firstCard.getAttribute('data-variant-id');
          console.log('Using card data-variant-id:', variantId);
          
          if (!variantId || variantId === 'undefined' || variantId === 'null') {
            // Last resort: radio button value
            const radio = firstCard.querySelector('input[type="radio"]');
            variantId = radio ? radio.value : null;
            console.log('Using radio button value:', variantId);
          }
        }
        
        // Validate variant ID
        if (!variantId || variantId === 'undefined' || variantId === 'null' || variantId === '') {
          console.error('No valid variant ID found!');
          console.log('Debug info:', {
            cardDataVariantId: firstCard.getAttribute('data-variant-id'),
            radioValue: firstCard.querySelector('input[type="radio"]')?.value,
            hasColorSwatches: !!firstCard.querySelector('.free-gift-card__color-swatch'),
            firstSwatchVariantId: firstCard.querySelector('.free-gift-card__color-swatch')?.getAttribute('data-variant-id')
          });
          FreeGiftUpsell.globalProcessing = false;
          return;
        }
        
        if (variantId) {
          // Convert variant ID to integer for Shopify API
          const variantIdInt = parseInt(variantId, 10);
          
          // Validate the integer conversion
          if (isNaN(variantIdInt)) {
            console.error('Failed to convert variant ID to integer:', variantId);
            FreeGiftUpsell.globalProcessing = false;
            return;
          }
          
          console.log('🎁 ATTEMPTING TO ADD FREE GIFT');
          console.log('   Variant ID:', variantIdInt);
          console.log('   Source:', selectedSwatch ? 'color swatch' : 'radio button');
          
          const requestBody = {
            items: [{
              id: variantIdInt,
              quantity: 1,
              properties: {
                _is_free_gift: true
              }
            }]
          };
          
          console.log('   Request body:', JSON.stringify(requestBody));
          
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
          });
          
          console.log('   Response status:', response.status);
          
          if (response.ok) {
            const addResult = await response.json();
            console.log('✅ FREE GIFT ADDED SUCCESSFULLY!');
            console.log('   Added items:', addResult.items?.length || 'unknown');
            
            // Mark as added immediately
            FreeGiftUpsell.hasAutoAdded = true;
            FreeGiftUpsell.markGiftAdded(variantIdInt);
            
            // Select the first swatch/radio visually
            if (selectedSwatch) {
              // Manually trigger selection visual update
              const selector = selectedSwatch.closest('.free-gift-card__color-selector');
              if (selector) {
                selector.querySelectorAll('.free-gift-card__color-swatch').forEach(s => {
                  s.classList.remove('is-selected');
                  const checkmark = s.querySelector('.free-gift-card__checkmark');
                  if (checkmark) checkmark.remove();
                });
                
                selectedSwatch.classList.add('is-selected');
                const checkmark = document.createElement('span');
                checkmark.className = 'free-gift-card__checkmark';
                checkmark.innerHTML = `
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12l5 5 9-9" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                `;
                selectedSwatch.appendChild(checkmark);
              }
            }
            
            // Also update radio button
            const radio = firstCard.querySelector('input[type="radio"]');
            if (radio) {
              radio.value = variantId;
              radio.checked = true;
              this.updateSelectedState();
            }
            
            // Trigger cart update
            if (this.cartDrawer) {
              this.refreshCartDrawer();
            }
            
            // Dispatch cart change event to notify other components
            document.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: { source: 'free-gift-auto-add' }
            }));
            
            // Wait for cart drawer to update
            await new Promise(resolve => setTimeout(resolve, 1200));
            
            // Clear any error messages that may have appeared
            this.clearFreeGiftErrors();
            
            // Don't disable UI - keep cards interactive so users can change selection
            console.log('Threshold gift added successfully - cards remain interactive');
          } else {
            const errorText = await response.text();
            console.error('❌ FAILED TO ADD FREE GIFT');
            console.error('   Status:', response.status);
            console.error('   Error:', errorText);
            
            // Don't set hasAutoAdded to true if it failed - allow retry
            FreeGiftUpsell.hasAutoAdded = false;
            
            // Try parsing error for more details
            try {
              const errorJson = JSON.parse(errorText);
              console.error('   Error details:', errorJson);
            } catch (e) {
              // Not JSON, already logged as text
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ EXCEPTION while auto-adding threshold gift:', error);
      FreeGiftUpsell.hasAutoAdded = false; // Allow retry on exception
    } finally {
      FreeGiftUpsell.globalProcessing = false;
    }
  }

  setupColorSwatches() {
    const colorSwatches = this.querySelectorAll('.free-gift-card__color-swatch');
    
    console.log('Setting up color swatches, found:', colorSwatches.length);
    
    // Debug: Log all variant IDs
    colorSwatches.forEach(swatch => {
      const variantId = swatch.getAttribute('data-variant-id');
      const optionValue = swatch.getAttribute('data-option-value');
      console.log('Swatch:', optionValue, '-> Variant ID:', variantId);
    });
    
    colorSwatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Check if threshold mode is disabled
        if (this.isThresholdModeDisabled()) {
          console.log('Threshold not met, cannot select variant');
          return;
        }
        
        if (e.currentTarget.classList.contains('is-disabled')) return;
        
        const productId = e.currentTarget.getAttribute('data-product-id');
        const variantId = e.currentTarget.getAttribute('data-variant-id');
        const card = e.currentTarget.closest('.free-gift-card');
        
        console.log('Color swatch clicked - Variant ID:', variantId, 'Product ID:', productId);

        // Re-selecting a colour after removing the gift is an active opt-in.
        FreeGiftUpsell.clearDeclined();
        FreeGiftUpsell.setSelectedVariantId(variantId);

        this.selectSwatchVisual(e.currentTarget);

        // Update variant ID in card and radio button
        if (card) {
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.value = variantId;
            radio.checked = true;

            // Update selected state
            this.updateSelectedState();

            if (this.confirmMode) {
              // Confirm / checkout-add mode: only update the selection. The cart
              // changes when checkout (or the add button) is pressed.
            } else {
              // Trigger change with correct variant ID - use a small delay to ensure value is updated
              setTimeout(() => {
                this.handleChange({ target: radio });
              }, 10);
            }
          }
        }
      });
    });
  }

  async handleRadioChange(event) {
    // Check if threshold mode is disabled
    if (this.isThresholdModeDisabled()) {
      console.log('Threshold not met, cannot select gift');
      event.preventDefault();
      return;
    }
    
    // Find the card that was clicked
    const radio = event.target;
    const card = radio.closest('.free-gift-card');
    
    if (!card) {
      console.error('Could not find card for radio button');
      return;
    }

    // Confirm mode: selecting a card just updates the selection. No cart write.
    if (this.confirmMode) {
      FreeGiftUpsell.clearDeclined();
      const confirmColorSelector = card.querySelector('.free-gift-card__color-selector');
      if (confirmColorSelector) {
        // Keep the customer's colour if one is already chosen / persisted.
        // Only fall back to the preferred (black) default when nothing is selected.
        const selectedSwatch = confirmColorSelector.querySelector('.free-gift-card__color-swatch.is-selected:not(.is-disabled)');
        const persistedId = FreeGiftUpsell.getSelectedVariantId();
        const persistedSwatch = persistedId
          ? confirmColorSelector.querySelector(`.free-gift-card__color-swatch[data-variant-id="${persistedId}"]:not(.is-disabled)`)
          : null;
        const swatchToUse = selectedSwatch || persistedSwatch || this.getPreferredSwatch(card);
        if (swatchToUse) {
          this.selectSwatchVisual(swatchToUse);
          FreeGiftUpsell.setSelectedVariantId(swatchToUse.getAttribute('data-variant-id'));
        }
      } else {
        FreeGiftUpsell.setSelectedVariantId(this.getCardVariantId(card));
      }
      this.updateSelectedState();
      return;
    }
    
    // Check if this product has color swatches
    const colorSelector = card.querySelector('.free-gift-card__color-selector');
    
    if (colorSelector) {
      // Product has color variants - automatically select the first available one
      const firstAvailableSwatch = colorSelector.querySelector('.free-gift-card__color-swatch:not(.is-disabled)');
      
      if (firstAvailableSwatch) {
        const variantId = firstAvailableSwatch.getAttribute('data-variant-id');
        const variantImage = firstAvailableSwatch.getAttribute('data-variant-image');
        const productId = firstAvailableSwatch.getAttribute('data-product-id');
        
        console.log('Auto-selecting first color variant:', variantId);
        
        // Update swatch selection visually
        colorSelector.querySelectorAll('.free-gift-card__color-swatch').forEach(s => {
          s.classList.remove('is-selected');
          const checkmark = s.querySelector('.free-gift-card__checkmark');
          if (checkmark) checkmark.remove();
        });
        
        firstAvailableSwatch.classList.add('is-selected');
        const checkmark = document.createElement('span');
        checkmark.className = 'free-gift-card__checkmark';
        checkmark.innerHTML = `
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 12l5 5 9-9" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        firstAvailableSwatch.appendChild(checkmark);
        
        // Update the radio button value to the selected color variant
        radio.value = variantId;
        card.setAttribute('data-variant-id', variantId);
        
        // Update product image if available
        const productImage = card.querySelector(`[data-product-featured-image="${productId}"]`);
        if (productImage && variantImage) {
          productImage.src = variantImage;
        }
      }
    }
    
    // Now proceed to add to cart with the correct variant
    this.handleChange(event);
  }

  // ============================================================
  // CONFIRM MODE (gift is added only when the button is pressed)
  // ============================================================

  // Resolve the selected variant id for a given card (swatch -> data-variant-id -> radio)
  getCardVariantId(card) {
    if (!card) return null;
    const fromCard = card.getAttribute('data-variant-id');
    if (fromCard && fromCard !== 'undefined' && fromCard !== 'null' && fromCard !== '') {
      return fromCard;
    }
    const radio = card.querySelector('input[type="radio"]');
    return radio && radio.value && radio.value !== 'undefined' ? radio.value : null;
  }

  // Get the variant id of the free gift currently in the cart (or null)
  async getCartGiftVariantId() {
    try {
      const cart = await fetch('/cart.js').then(res => res.json());
      const gift = cart.items.find(item => item.properties && item.properties._is_free_gift);
      return gift ? gift.variant_id.toString() : null;
    } catch (error) {
      return null;
    }
  }

  // Initial setup for confirm mode
  async initConfirmMode() {
    // Make sure the hide/show state matches the actual cart on (re)mount
    await this.syncFulfilledVisibility();

    const cartGiftVariantId = await this.getCartGiftVariantId();

    if (cartGiftVariantId) {
      // A gift is already in the cart - reflect that selection in the UI
      FreeGiftUpsell.setSelectedVariantId(cartGiftVariantId);
      this.selectCardByVariant(cartGiftVariantId);
    } else if (!this.isThresholdModeDisabled()) {
      // Prefer the colour the customer already picked this session (survives the
      // cart drawer wiping and re-inserting this element on every cart change).
      // Only fall back to black when they have never chosen a colour.
      const persistedId = FreeGiftUpsell.getSelectedVariantId();
      if (persistedId && this.selectCardByVariant(persistedId)) {
        return;
      }
      this.preselectFirstChoice();
    }
  }

  // Return the swatch that should be selected by default for a card: prefer a
  // black/dark colour when present, otherwise the first available swatch.
  getPreferredSwatch(card) {
    if (!card) return null;
    const swatches = Array.from(card.querySelectorAll('.free-gift-card__color-swatch:not(.is-disabled)'));
    if (!swatches.length) return null;
    const blackRegex = /(black|zwart|noir|schwarz|nero|negro|preto|svart)/i;
    const black = swatches.find(s => blackRegex.test(s.getAttribute('data-option-value') || ''));
    return black || swatches[0];
  }

  // Visually pre-select the default swatch in each card (no cart write).
  // Never overrides a colour the customer (or a persisted restore) already chose —
  // that was snapping beige back to black after cart-drawer remounts.
  preselectFirstChoice() {
    const persistedId = FreeGiftUpsell.getSelectedVariantId();
    if (persistedId && this.selectCardByVariant(persistedId)) {
      return;
    }

    this.cards.forEach((card, index) => {
      const radio = card.querySelector('input[type="radio"]');
      if (radio && index === 0) radio.checked = true;

      const hasSelected = card.querySelector('.free-gift-card__color-swatch.is-selected:not(.is-disabled)');
      if (hasSelected) {
        FreeGiftUpsell.setSelectedVariantId(hasSelected.getAttribute('data-variant-id'));
        return;
      }

      const preferredSwatch = this.getPreferredSwatch(card);
      if (preferredSwatch) {
        this.selectSwatchVisual(preferredSwatch);
        FreeGiftUpsell.setSelectedVariantId(preferredSwatch.getAttribute('data-variant-id'));
      }
    });
    this.updateSelectedState();
  }

  // ============================================================
  // CHECKOUT-ADD MODE (gift is added when checkout is pressed)
  // ============================================================

  // Locate the cart drawer's checkout submit button and intercept its click.
  setupCheckoutAdd() {
    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    const form = drawer ? drawer.querySelector('form.buy-buttons') : null;
    const button = form ? form.querySelector('[name="checkout"]') : null;
    if (!button) return;

    this._checkoutButton = button;
    this._onCheckoutClick = this.handleCheckoutClick.bind(this);
    // Capture phase so we run before the form's native submit is committed.
    button.addEventListener('click', this._onCheckoutClick, true);
  }

  teardownCheckoutAdd() {
    if (this._checkoutButton && this._onCheckoutClick) {
      this._checkoutButton.removeEventListener('click', this._onCheckoutClick, true);
    }
    this._checkoutButton = null;
    this._onCheckoutClick = null;
  }

  async handleCheckoutClick(event) {
    // Our own re-submit must pass straight through.
    if (this._proceedingCheckout) return;

    // Nothing to add while the threshold is not met.
    if (this.isThresholdModeDisabled()) return;

    // In checkout-add mode the gift is re-offered on every checkout while the
    // threshold is met, so a prior removal must not block this path. In other
    // modes, respect an explicit decline until the customer re-selects a colour.
    if (!this.checkoutAddMode && FreeGiftUpsell.isDeclined()) {
      console.log('Skipping checkout gift add: customer removed the gift (re-select a colour to opt back in)');
      return;
    }

    // Resolve the currently selected card + variant. Prefer the session-persisted
    // colour so a remount that briefly showed the Liquid default (black) cannot
    // override a beige choice the customer already made.
    const persistedId = FreeGiftUpsell.getSelectedVariantId();
    if (persistedId) {
      this.selectCardByVariant(persistedId);
    }

    const card = this.querySelector('.free-gift-card.is-selected')
      || this.querySelector('.free-gift-card');
    const variantId = this.getCardVariantId(card) || persistedId;
    if (!variantId) return;

    const button = event.currentTarget;
    const form = button.form || button.closest('form');

    // Take over: stop the native submit, add the gift, then resume checkout.
    event.preventDefault();
    event.stopImmediatePropagation();

    this._proceedingCheckout = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const cart = await fetch('/cart.js').then(res => res.json());
      const hasGift = cart.items.some(item => item.properties && item.properties._is_free_gift);

      if (!hasGift) {
        const variantIdInt = parseInt(variantId, 10);
        if (!isNaN(variantIdInt)) {
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{
                id: variantIdInt,
                quantity: 1,
                properties: { _is_free_gift: true }
              }]
            })
          });
          if (addResponse.ok) {
            FreeGiftUpsell.markGiftAdded(variantIdInt);
            FreeGiftUpsell.setSelectedVariantId(variantIdInt);
          }
        }
      }
    } catch (error) {
      // Never block checkout because the gift add failed.
      console.warn('Free gift could not be added before checkout:', error);
    }

    // Resume checkout. requestSubmit keeps the submitter so the "checkout" param
    // is sent and Shopify redirects to checkout (now including the gift).
    if (form && form.requestSubmit) {
      form.requestSubmit(button);
    } else if (button.click) {
      button.click();
    } else {
      window.location.href = '/checkout';
    }
  }

  // Find the card/swatch matching a variant id and mark it selected (no cart write)
  selectCardByVariant(variantId) {
    const target = String(variantId);
    let matched = false;

    this.cards.forEach(card => {
      const radio = card.querySelector('input[type="radio"]');
      const swatches = card.querySelectorAll('.free-gift-card__color-swatch');

      if (swatches.length > 0) {
        swatches.forEach(swatch => {
          if (swatch.getAttribute('data-variant-id') === target) {
            this.selectSwatchVisual(swatch);
            card.setAttribute('data-variant-id', target);
            if (radio) { radio.value = target; radio.checked = true; }
            matched = true;
          }
        });
      } else if (card.getAttribute('data-variant-id') === target || (radio && radio.value === target)) {
        if (radio) radio.checked = true;
        matched = true;
      }
    });

    this.updateSelectedState();
    return matched;
  }

  // Apply the selected visual state to a swatch (checkmark + selected class + image)
  selectSwatchVisual(swatch) {
    if (!swatch) return;
    const selector = swatch.closest('.free-gift-card__color-selector');
    const card = swatch.closest('.free-gift-card');

    if (selector) {
      selector.querySelectorAll('.free-gift-card__color-swatch').forEach(s => {
        s.classList.remove('is-selected');
        const cm = s.querySelector('.free-gift-card__checkmark');
        if (cm) cm.remove();
      });
    }

    swatch.classList.add('is-selected');
    if (!swatch.querySelector('.free-gift-card__checkmark')) {
      const checkmark = document.createElement('span');
      checkmark.className = 'free-gift-card__checkmark';
      checkmark.innerHTML = `
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 12l5 5 9-9" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
      swatch.appendChild(checkmark);
    }

    // Sync the variant id + image onto the card and its radio
    const variantId = swatch.getAttribute('data-variant-id');
    const variantImage = swatch.getAttribute('data-variant-image');
    const productId = swatch.getAttribute('data-product-id');
    if (variantId) {
      FreeGiftUpsell.setSelectedVariantId(variantId);
    }
    if (card && variantId) {
      card.setAttribute('data-variant-id', variantId);
      const radio = card.querySelector('input[type="radio"]');
      if (radio) radio.value = variantId;
      const productImage = card.querySelector(`[data-product-featured-image="${productId}"]`);
      if (productImage && variantImage) productImage.src = variantImage;
    }
  }

  // Toggle a card's add button into a loading state (shows a spinner)
  setButtonLoading(button, isLoading) {
    if (!button) return;
    button.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
  }

  // Button handler: add the selected gift for the clicked card
  async confirmGift(event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }

    const button = event ? event.currentTarget : null;
    if (this.isThresholdModeDisabled()) return;
    if (this.isProcessing || FreeGiftUpsell.globalProcessing) return;
    if (button && button.disabled) return;

    const card = button ? button.closest('.free-gift-card') : this.querySelector('.free-gift-card');
    if (!card) return;

    // Pressing the add button is the customer asking for the gift, so an earlier
    // removal no longer stands.
    FreeGiftUpsell.clearDeclined();

    // Make this card the active selection
    const radio = card.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    // Ensure a colour is chosen; default to the first available swatch
    if (card.querySelector('.free-gift-card__color-selector') &&
        !card.querySelector('.free-gift-card__color-swatch.is-selected')) {
      const firstSwatch = card.querySelector('.free-gift-card__color-swatch:not(.is-disabled)');
      if (firstSwatch) this.selectSwatchVisual(firstSwatch);
    }
    this.updateSelectedState();

    const variantId = this.getCardVariantId(card);
    if (!variantId) return;

    this.setButtonLoading(button, true);

    // Add (or replace) the gift. The drawer re-renders via cart:refresh, which removes
    // this whole section because the gift is now in the cart.
    await this.handleChange({ target: { value: variantId }, preventDefault() {} });
  }

  // Remove any free gift from the cart when the cart falls below the threshold
  async removeGiftIfBelowThreshold() {
    if (this.isProcessing || FreeGiftUpsell.globalProcessing) return;

    try {
      const cart = await fetch('/cart.js').then(res => res.json());
      const giftLines = [];
      cart.items.forEach((item, index) => {
        if (item.properties && item.properties._is_free_gift) {
          giftLines.push(index + 1);
        }
      });
      if (giftLines.length === 0) return;

      // We are pulling the gift because the cart dropped below the threshold.
      // That is our doing, not a customer decline.
      this.selfRemoving = true;
      FreeGiftUpsell.clearGiftAdded();
      FreeGiftUpsell.globalProcessing = true;

      // Remove from the bottom up so line numbers stay valid
      giftLines.sort((a, b) => b - a);
      for (const line of giftLines) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: 0 })
        });
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      FreeGiftUpsell.hasAutoAdded = false;
      this.refreshCartDrawer();
    } catch (error) {
      console.warn('Could not remove free gift below threshold:', error);
    } finally {
      setTimeout(() => {
        this.selfRemoving = false;
        FreeGiftUpsell.globalProcessing = false;
      }, 600);
    }
  }

  async handleChange(event) {
    // Check if threshold mode is disabled
    if (this.isThresholdModeDisabled()) {
      console.log('Threshold not met, cannot select gift');
      event.preventDefault();
      return;
    }
    
    // Prevent duplicate processing
    if (this.isProcessing || FreeGiftUpsell.globalProcessing) {
      console.log('Already processing, skipping...');
      return;
    }

    // Get the variant ID from the radio button value
    const selectedVariantId = event.target.value;
    
    // Validate we have a proper variant ID
    if (!selectedVariantId || selectedVariantId === 'undefined') {
      console.error('Invalid variant ID:', selectedVariantId);
      return;
    }
    
    console.log('Changing free gift to variant:', selectedVariantId);

    // Reaching here always follows a deliberate selection by the customer, so an
    // earlier removal no longer applies.
    FreeGiftUpsell.clearDeclined();

    this.updateSelectedState();
    this.isProcessing = true;
    FreeGiftUpsell.globalProcessing = true;
    this.setLoading(true);

    try {
      // Get fresh cart state
      let currentCart = await fetch('/cart.js').then(res => res.json());
      let isSelectedGiftInCart = false;

      // Check current state of free gifts
      currentCart.items.forEach(item => {
        if (item.properties && item.properties._is_free_gift) {
          if (item.variant_id.toString() === selectedVariantId.toString()) {
            isSelectedGiftInCart = true;
            console.log('Selected gift already in cart');
          }
        }
      });

      // If the selected gift is already in cart, do nothing
      if (isSelectedGiftInCart) {
        console.log('Selected gift already in cart, no action needed');
        return;
      }

      // Remove any existing free gifts first - fetch FRESH cart state right before
      console.log('Fetching fresh cart state before removal...');
      currentCart = await fetch('/cart.js').then(res => res.json());
      
      // Find free gift items and their line numbers (1-based index)
      const freeGiftLines = [];
      currentCart.items.forEach((item, index) => {
        if (item.properties && item.properties._is_free_gift) {
          freeGiftLines.push({
            line: index + 1, // Shopify uses 1-based line numbers
            key: item.key,
            variantId: item.variant_id
          });
          console.log('Found free gift to remove - Line:', index + 1, 'Variant:', item.variant_id);
        }
      });

      // Remove any existing free gifts if found
      if (freeGiftLines.length > 0) {
        console.log('Removing', freeGiftLines.length, 'free gift(s)...');
        // This removal is ours (we are swapping the colour), not the customer's
        this.selfRemoving = true;
        FreeGiftUpsell.clearGiftAdded();
        
        // Sort by line number descending to remove from bottom up
        // This prevents line number shifts during removal
        freeGiftLines.sort((a, b) => b.line - a.line);
        
        // Remove each gift one by one using line-based removal
        for (const gift of freeGiftLines) {
          try {
            // Use line-based removal which is more reliable
            const removeResponse = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                line: gift.line,
                quantity: 0 
              })
            });
            
            if (removeResponse.ok) {
              console.log('✓ Removed gift at line', gift.line);
              // Small delay between removals
              await new Promise(resolve => setTimeout(resolve, 100));
            } else {
              // Silently handle errors - error cleaner will remove any visible errors
              console.warn('⚠ Failed to remove gift at line', gift.line, '- continuing');
            }
          } catch (error) {
            // Silently handle exceptions - error cleaner will remove any visible errors
            console.warn('⚠ Exception removing gift at line', gift.line, '- continuing');
          }
        }
        
        // Wait for all removals to fully process
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Clear any lingering error messages
        this.clearFreeGiftErrors();
      }

      // Add the selected free gift
      const variantIdInt = parseInt(selectedVariantId, 10);
      console.log('Adding new gift with variant ID:', variantIdInt);
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantIdInt,
            quantity: 1,
            properties: {
              _is_free_gift: true
            }
          }]
        })
      });

      if (!addResponse.ok) {
        const errorText = await addResponse.text();
        console.error('Failed to add new gift:', errorText);
        throw new Error('Failed to add new gift');
      }

      console.log('Gift changed successfully');
      FreeGiftUpsell.hasAutoAdded = true;
      FreeGiftUpsell.markGiftAdded(variantIdInt);
      
      // Clear any error messages that may have appeared
      this.clearFreeGiftErrors();
      
      // Trigger cart update FIRST
      if (this.cartDrawer) {
        this.refreshCartDrawer();
      }
      
      // Update progress bars
      await this.updateProgressBars();

      // Wait for cart drawer to update
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Clear errors one more time after cart refresh
      this.clearFreeGiftErrors();
      
      // Don't disable UI - users can continue to change their selection
      console.log('Gift changed successfully - cards remain interactive');

    } catch (error) {
      console.error('Failed to update cart with free gift:', error);
      // Force a cart refresh to sync state
      if (this.cartDrawer) {
        await new Promise(resolve => setTimeout(resolve, 600));
        this.refreshCartDrawer();
      }
    } finally {
      this.setLoading(false);
      // Delay before allowing next operation
      setTimeout(() => {
        this.isProcessing = false;
        this.selfRemoving = false;
        FreeGiftUpsell.globalProcessing = false;
      }, 1000);
    }
  }
}

if (!customElements.get('free-gift-upsell')) {
  customElements.define('free-gift-upsell', FreeGiftUpsell);
}

// ------------------------------------------------------------------
// Back/forward-cache (bfcache) recovery.
//
// When the customer presses checkout and then uses the browser's back button,
// the storefront is restored from the bfcache frozen exactly as it was left -
// including any "busy"/"loading" states that were mid-flight when they navigated
// away. That stale state is what leaves the cart's quantity selector unresponsive
// ("frozen") on return. On a persisted pageshow we clear those stuck states and
// reset our own processing flags so the cart is interactive again immediately.
// ------------------------------------------------------------------
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  // Reset our gift processing flags so a subsequent checkout still adds the gift.
  try {
    FreeGiftUpsell.globalProcessing = false;
    document.querySelectorAll('free-gift-upsell').forEach((el) => {
      el.isProcessing = false;
      el._proceedingCheckout = false;
      el.removeAttribute('loading');
    });
  } catch (e) {
    // no-op
  }

  // Clear stuck busy/loading states across the cart UI (drawer + cart page).
  const scopes = document.querySelectorAll('cart-drawer, .cart-drawer, main, body');
  scopes.forEach((scope) => {
    scope.querySelectorAll('[aria-busy="true"]').forEach((el) => el.removeAttribute('aria-busy'));
    scope.querySelectorAll('pill-loader[aria-busy]').forEach((el) => el.removeAttribute('aria-busy'));
    scope.querySelectorAll('[loading]').forEach((el) => {
      if (el.matches('cart-drawer, free-gift-upsell, .cart-drawer, form, line-item')) {
        el.removeAttribute('loading');
      }
    });
    // Re-enable any quantity controls that may have been left disabled.
    scope.querySelectorAll('quantity-selector button[disabled], line-item-quantity input[disabled]').forEach((el) => {
      el.disabled = false;
    });
  });

  // Ask the cart drawer to rebuild from the server so its contents are fresh and
  // fully interactive (idempotent alongside the theme's own bfcache refresh).
  document.dispatchEvent(new CustomEvent('cart:refresh'));
});
/* --------------------------------------------------------------------------------------------------------------------
   Kopstorm bundle gift orphan guard
   A free gift added by the bundle picker carries the line property _kop_bundle_gift = <handle of the parent product>
   (older lines: the parent variant id). When that parent is no longer in the cart (removed in the drawer, on /cart or in checkout and then back), the gift is
   removed too. Runs after every cart change, debounced, and never loops: a removal triggers one more check which then
   finds nothing to do.
   -------------------------------------------------------------------------------------------------------------------- */
(function () {
  'use strict';
  var root = (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
  var timer = null;
  var running = false;

  function check() {
    if (running) return;
    running = true;
    fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var orphans = items.filter(function (it) {
          var parent = it.properties && it.properties._kop_bundle_gift;
          if (!parent) return false;
          parent = String(parent);
          var parentId = /^[0-9]+$/.test(parent) ? parseInt(parent, 10) : 0;
          return !items.some(function (o) {
            if (o.properties && o.properties._kop_bundle_gift) return false;
            return o.handle === parent || (parentId && o.variant_id === parentId);
          });
        });
        if (!orphans.length) { running = false; return; }
        var updates = {};
        orphans.forEach(function (it) { updates[it.key] = 0; });
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function () {
          running = false;
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        });
      })
      .catch(function () { running = false; });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(check, 400);
  }

  ['cart:change', 'cart:refresh', 'cart:update', 'variant:add'].forEach(function (ev) {
    document.addEventListener(ev, schedule);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
